import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, forkJoin, map, of, switchMap, tap, timeout } from 'rxjs';

import { environment } from '../../environments/environment';

/** What RFI is doing with the estimate when the original is kept. */
export type VendorEstimateOriginalSavedFor = 'counter' | 'reject_for_resubmission';

/**
 * Keeps the vendor's original estimate, exactly as the vendor submitted it, the first time RFI sends it back (Reject for
 * Resubmission, or a negotiating counter): `POST api/v1/admin/vendor-estimates/{estimateKey}/original`.
 *
 * It is saved ONCE per estimate: this service first asks Job Ops whether the original is already there
 * (`GET .../original/status`) and only saves when it is not. Job Ops also ignores a repeated save, so a race between two admins is
 * harmless. It must run BEFORE the send-back, which can change the estimate's lines.
 *
 * Best-effort: a failure never reaches the caller as an error, so it can never stop the admin's reject or counter. It
 * is logged to the console and reported as `false`.
 */
@Injectable({ providedIn: 'root' })
export class VendorEstimateOriginalService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api/v1/admin/vendor-estimates`;
  private static readonly TIMEOUT_MS = 20_000;

  /** Estimates whose original is known to be saved (this page session), so the same estimate is not asked about again. */
  private readonly knownSaved = new Set<string>();

  /**
   * Is the original of this estimate already saved? true / false from Job Ops; null when it could not be told (offline, an older
   * API build without the status route). Never errors.
   */
  isSaved(estimateKey: string): Observable<boolean | null> {
    if (!estimateKey) return of(null);
    if (this.knownSaved.has(estimateKey)) return of(true);
    return this.http.get<{ status?: boolean; data?: { saved?: boolean } | null }>(`${this.base}/${estimateKey}/original/status`).pipe(
      timeout(VendorEstimateOriginalService.TIMEOUT_MS),
      map((res) => {
        const saved = res?.data?.saved;
        return res?.status === false || typeof saved !== 'boolean' ? null : saved;
      }),
      tap((saved) => {
        if (saved) this.knownSaved.add(estimateKey);
      }),
      catchError(() => of(null)),
    );
  }

  /**
   * Saves the original only when it is not saved yet. Emits once: true when it is saved (now or before), false when it could not be
   * saved. Never errors.
   */
  saveOnce(estimateKey: string, savedFor: VendorEstimateOriginalSavedFor): Observable<boolean> {
    if (!estimateKey) return of(false);
    return this.isSaved(estimateKey).pipe(
      // "Could not tell" goes ahead and saves: Job Ops ignores a repeated save, so that is safe.
      switchMap((saved) => (saved === true ? of(true) : this.save(estimateKey, savedFor))),
    );
  }

  private save(estimateKey: string, savedFor: VendorEstimateOriginalSavedFor): Observable<boolean> {
    return this.http
      .post<{ status?: boolean }>(`${this.base}/${estimateKey}/original`, { savedFor })
      .pipe(
        timeout(VendorEstimateOriginalService.TIMEOUT_MS),
        map((res) => res?.status !== false),
        tap((ok) => {
          if (ok) this.knownSaved.add(estimateKey);
        }),
        catchError((err) => {
          console.warn('The original vendor estimate was not saved.', estimateKey, err?.status ?? err);
          return of(false);
        }),
      );
  }

  /** The same for several estimates (each is saved on its own). Emits once, after all of them answered. Never errors. */
  saveOnceForAll(estimateKeys: readonly string[], savedFor: VendorEstimateOriginalSavedFor): Observable<void> {
    const keys = [...new Set(estimateKeys.filter((k) => !!k))];
    if (keys.length === 0) return of(undefined);
    return forkJoin(keys.map((key) => this.saveOnce(key, savedFor))).pipe(map(() => undefined));
  }
}
